import { useMemo, useState, type ReactNode } from "react";
import { ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, CircleDot, FileText, Plus, Search } from "lucide-react";
import { ApiError } from "@ncp/api-client";
import { FormRenderer } from "@ncp/forms";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, Field, InlineAlert, Input, NativeSelect, Skeleton, StepLetterSheets, Switch, Textarea, cn, saveFailureText, useMicrocopy } from "@ncp/ui";
import {
  useCaseDetail, useCaseRows, useCreateRecord, useFireTransition, useMoveLetters, useReadMoveLetters, useRecordDetail, useRecordPage, useRecordTypeView, useReportRun, useScreen, useServiceIntake, useStartService,
  type CaseRow, type Surface,
} from "./api";
import { ReportView, formatCell } from "./ReportView";
import { screenText } from "./text";
import { pick, type Locale, type RecordField, type RecordIssue, type ScreenBlock, type ScreenDefinitionView } from "./types";

/** What the screen is focused on: a record picked in a list, or a case. Hosts keep it in the URL (`?record=…`, `?case=…`). */
export interface ScreenSelection { record?: string | null; case?: string | null }

export interface ScreenRendererProps {
  surface: Surface;
  locale: Locale;
  /** A published screen to load… */
  screenKey?: string;
  /** …or a definition to render as is (the Screen Builder's live preview). */
  definition?: ScreenDefinitionView;
  selection?: ScreenSelection;
  onSelect?: (next: ScreenSelection) => void;
  /** Where a case opens in the host app (the back office's case workspace, the portal's case page). */
  caseHref?: (id: string) => string;
  /** Preview: forms render but do not submit. */
  preview?: boolean;
}

const issueField = (path: string) => path.replace(/^\$\.?/, "").replace(/^\//, "").split(/[/.[]/)[0] ?? "";
const date = (value: string, locale: Locale) => new Intl.DateTimeFormat(locale === "ar" ? "ar-AE" : "en-GB", { dateStyle: "medium" }).format(new Date(value));
const problemOf = (e: unknown) => (e instanceof ApiError ? e.problem : undefined);

/** Renders a screen described as data: record lists, forms and details, reports and figures, service intake and case lists and detail. */
export function ScreenRenderer({ surface, locale, screenKey, definition, selection: controlled, onSelect, caseHref, preview }: ScreenRendererProps) {
  const t = screenText(locale);
  const loaded = useScreen(definition ? undefined : screenKey, surface);
  const screen = definition ?? loaded.data;
  const [internal, setInternal] = useState<ScreenSelection>({});
  const selection = controlled ?? internal;
  const select = (next: ScreenSelection) => (onSelect ? onSelect(next) : setInternal(next));

  if (!definition && loaded.isLoading) return <div className="space-y-3"><Skeleton className="h-10 w-1/3" /><Skeleton className="h-64" /></div>;
  if (!screen) return <EmptyState icon={FileText} title={t("notFound")} description={t("notFoundBody")} />;

  const ctx: BlockCtx = { surface, locale, selection, select, caseHref, preview: !!preview };
  const groups = groupKpis(screen.blocks ?? []);
  return (
    <div className="space-y-4" dir={locale === "ar" ? "rtl" : "ltr"} data-testid={`screen-${screen.key}`}>
      {preview ? <InlineAlert tone="info">{t("preview")}</InlineAlert> : null}
      {groups.map((g, i) => Array.isArray(g)
        ? <div key={i} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{g.map((b, j) => <KpiBlock key={j} block={b} ctx={ctx} />)}</div>
        : <Block key={i} block={g} ctx={ctx} />)}
    </div>
  );
}

interface BlockCtx { surface: Surface; locale: Locale; selection: ScreenSelection; select: (next: ScreenSelection) => void; caseHref?: (id: string) => string; preview: boolean }
type KpiBlockDef = Extract<ScreenBlock, { type: "kpi" }>;

function groupKpis(blocks: ScreenBlock[]): Array<ScreenBlock | KpiBlockDef[]> {
  const out: Array<ScreenBlock | KpiBlockDef[]> = [];
  for (const b of blocks) {
    const last = out[out.length - 1];
    if (b.type === "kpi") { if (Array.isArray(last)) last.push(b); else out.push([b]); }
    else out.push(b);
  }
  return out;
}

function Block({ block, ctx }: { block: ScreenBlock; ctx: BlockCtx }) {
  const { locale } = ctx;
  switch (block.type) {
    case "heading": return (
      <div>
        <h2 className="text-lg font-semibold">{pick(locale, block.text, block.textAr)}</h2>
        {block.body ? <p className="mt-0.5 text-sm text-muted-foreground">{pick(locale, block.body, block.bodyAr)}</p> : null}
      </div>
    );
    case "text": return <p className="whitespace-pre-line text-sm text-sand-800">{pick(locale, block.text, block.textAr)}</p>;
    case "recordList": return <RecordListBlock block={block} ctx={ctx} />;
    case "recordForm": return <RecordFormBlock block={block} ctx={ctx} />;
    case "recordDetail": return <RecordDetailBlock block={block} ctx={ctx} />;
    case "report": return <ReportBlock block={block} ctx={ctx} />;
    case "kpi": return <KpiBlock block={block} ctx={ctx} />;
    case "serviceStart": return <ServiceStartBlock block={block} ctx={ctx} />;
    case "caseList": return <CaseListBlock block={block} ctx={ctx} />;
    case "caseDetail": return <CaseDetailBlock block={block} ctx={ctx} />;
    default: return null;
  }
}

function Section({ title, description, action, children, testId }: { title?: ReactNode; description?: ReactNode; action?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <Card data-testid={testId}>
      {title || action ? (
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0 pb-3">
          <div>{title ? <CardTitle className="text-base">{title}</CardTitle> : null}{description ? <CardDescription>{description}</CardDescription> : null}</div>
          {action}
        </CardHeader>
      ) : null}
      <CardContent className={title || action ? undefined : "pt-5"}>{children}</CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------- records

function RecordListBlock({ block, ctx }: { block: Extract<ScreenBlock, { type: "recordList" }>; ctx: BlockCtx }) {
  const t = screenText(ctx.locale);
  const type = useRecordTypeView(block.recordType);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const take = block.pageSize ?? 20;
  const records = useRecordPage(block.recordType, q, page * take, take);
  const fields = useMemo(() => {
    const all = type.data?.fields ?? [];
    const chosen = block.columns?.length ? block.columns.map((k) => all.find((f) => f.key === k)).filter((f): f is RecordField => !!f) : all.filter((f) => f.listed);
    return chosen.slice(0, 6);
  }, [type.data, block.columns]);
  const total = records.data?.total ?? 0;
  const title = pick(ctx.locale, block.title ?? type.data?.name, block.titleAr ?? type.data?.nameAr);

  return (
    <Section title={title} description={t("records", { n: total })} testId={`block-recordList-${block.recordType}`}
      action={block.allowCreate && !ctx.preview ? <Button size="sm" variant="gold" onClick={() => setCreating((c) => !c)}><Plus /> {t("newRecord")}</Button> : null}>
      {creating ? (
        <div className="mb-4 rounded-xl border border-sand-200 bg-sand-50/60 p-3">
          <RecordFormInner recordType={block.recordType} ctx={ctx} onSaved={(id) => { setCreating(false); ctx.select({ ...ctx.selection, record: id }); }} onCancel={() => setCreating(false)} />
        </div>
      ) : null}
      <div className="relative mb-3 max-w-xs">
        <Search className="pointer-events-none absolute start-2.5 top-2.5 size-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder={t("search")} className="ps-8" />
      </div>
      {records.isLoading || type.isLoading ? <Skeleton className="h-40" /> : (records.data?.rows.length ?? 0) === 0 ? <p className="text-sm text-muted-foreground">{t("noRecords")}</p> : (
        <div className="overflow-x-auto rounded-xl border border-sand-200">
          <table className="w-full text-sm">
            <thead className="bg-sand-50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t("number")}</th>
                <th className="px-3 py-2 text-start font-medium">{t("title")}</th>
                {fields.map((f) => <th key={f.key} className="px-3 py-2 text-start font-medium">{pick(ctx.locale, f.label, f.labelAr)}</th>)}
                <th className="px-3 py-2 text-start font-medium">{t("updated")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand-100">
              {records.data!.rows.map((r) => (
                <tr key={r.id} onClick={() => ctx.select({ ...ctx.selection, record: r.id, case: r.caseId ?? ctx.selection.case })}
                  className={cn("cursor-pointer hover:bg-sand-50", ctx.selection.record === r.id && "bg-aegold-50/60")}>
                  <td className="px-3 py-2 font-mono text-xs" dir="ltr">{r.number}</td>
                  <td className="px-3 py-2 font-medium">{r.title}</td>
                  {fields.map((f) => <td key={f.key} className="px-3 py-2">{display(f, r.data?.[f.key], ctx.locale)}</td>)}
                  <td className="px-3 py-2 text-xs text-muted-foreground">{date(r.updatedAt, ctx.locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > take ? (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs">
          <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="rtl:rotate-180" /> {t("previous")}</Button>
          <span>{page + 1} / {Math.ceil(total / take)}</span>
          <Button size="sm" variant="ghost" disabled={(page + 1) * take >= total} onClick={() => setPage((p) => p + 1)}>{t("next")} <ChevronRight className="rtl:rotate-180" /></Button>
        </div>
      ) : null}
    </Section>
  );
}

function display(f: RecordField, v: unknown, locale: Locale): string {
  if (f.type === "choice") {
    const o = f.options?.find((x) => x.value === v);
    if (o) return pick(locale, o.label, o.labelAr);
  }
  if (f.type === "date" && typeof v === "string" && v) return date(v, locale);
  return formatCell(v, locale);
}

function RecordFormBlock({ block, ctx }: { block: Extract<ScreenBlock, { type: "recordForm" }>; ctx: BlockCtx }) {
  const type = useRecordTypeView(block.recordType);
  return (
    <Section title={pick(ctx.locale, block.title ?? type.data?.name, block.titleAr ?? type.data?.nameAr)} testId={`block-recordForm-${block.recordType}`}>
      <RecordFormInner recordType={block.recordType} fields={block.fields} submitLabel={pick(ctx.locale, block.submitLabel, block.submitLabelAr) || undefined} ctx={ctx} onSaved={(id) => ctx.select({ ...ctx.selection, record: id })} />
    </Section>
  );
}

function RecordFormInner({ recordType, fields: only, submitLabel, ctx, onSaved, onCancel }: { recordType: string; fields?: string[]; submitLabel?: string; ctx: BlockCtx; onSaved: (id: string) => void; onCancel?: () => void }) {
  const t = screenText(ctx.locale);
  const type = useRecordTypeView(recordType);
  const create = useCreateRecord();
  const [data, setData] = useState<Record<string, unknown>>({});
  const [issues, setIssues] = useState<RecordIssue[]>([]);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (type.isLoading) return <Skeleton className="h-32" />;
  const fields = (type.data?.fields ?? []).filter((f) => !only?.length || only.includes(f.key));
  const save = async () => {
    setError(null); setIssues([]); setSaved(null);
    try {
      const r = await create.mutateAsync({ type: recordType, data, locale: ctx.locale });
      setSaved(r.number); setData({}); onSaved(r.id);
    } catch (e) {
      const p = problemOf(e);
      setIssues((p?.issues ?? []) as RecordIssue[]);
      setError(p?.issues?.length ? t("fixProblems") : saveFailureText(e));
    }
  };
  return (
    <div className="space-y-3">
      <RecordInputs fields={fields} data={data} onChange={setData} issues={issues} locale={ctx.locale} draft={ctx.preview ? null : `screen:record:new:${recordType}`} />
      {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}
      {saved ? <InlineAlert tone="success">{t("saved", { number: saved })}</InlineAlert> : null}
      <div className="flex gap-2">
        <Button variant="gold" loading={create.isPending} disabled={ctx.preview} onClick={() => void save()} data-testid={`save-record-${recordType}`}>{submitLabel ?? t("save")}</Button>
        {onCancel ? <Button variant="ghost" onClick={onCancel}>{t("cancel")}</Button> : null}
      </div>
    </div>
  );
}

/** `draft` names the record the long texts are kept on the device under until the server has them (S 2.7). */
function RecordInputs({ fields, data, onChange, issues, locale, draft }: { fields: RecordField[]; data: Record<string, unknown>; onChange: (next: Record<string, unknown>) => void; issues: RecordIssue[]; locale: Locale; draft?: string | null }) {
  const set = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map((f) => {
        const err = issues.filter((i) => issueField(i.path) === f.key).map((i) => pick(locale, i.message, i.messageAr)).join(" ");
        const value = data[f.key];
        const common = { "data-testid": `field-${f.key}` };
        const input = (() => {
          switch (f.type) {
            case "longText": return <Textarea {...common} value={String(value ?? "")} dir="auto" onChange={(e) => set(f.key, e.target.value)} className="min-h-[88px]" draft={draft ? `${draft}:${f.key}` : null} />;
            case "number": case "money": return <Input {...common} value={value == null ? "" : String(value)} inputMode="decimal" dir="ltr" onChange={(e) => set(f.key, e.target.value)} />;
            case "date": return <Input {...common} type="date" value={String(value ?? "")} onChange={(e) => set(f.key, e.target.value)} />;
            case "boolean": return <div className="flex h-9 items-center"><Switch checked={value === true} onCheckedChange={(c) => set(f.key, c)} aria-label={pick(locale, f.label, f.labelAr)} /></div>;
            case "choice": return (
              <NativeSelect {...common} value={String(value ?? "")} onChange={(e) => set(f.key, e.target.value)}>
                <option value="">—</option>
                {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{pick(locale, o.label, o.labelAr)}</option>)}
              </NativeSelect>
            );
            case "email": return <Input {...common} type="email" dir="ltr" value={String(value ?? "")} onChange={(e) => set(f.key, e.target.value)} />;
            default: return <Input {...common} value={String(value ?? "")} dir="auto" onChange={(e) => set(f.key, e.target.value)} />;
          }
        })();
        return (
          <div key={f.key} className={f.type === "longText" ? "sm:col-span-2" : undefined}>
            <Field label={pick(locale, f.label, f.labelAr)} required={f.required} hint={pick(locale, f.help, f.helpAr) || undefined} error={err || undefined}>{input}</Field>
          </div>
        );
      })}
    </div>
  );
}

function RecordDetailBlock({ block, ctx }: { block: Extract<ScreenBlock, { type: "recordDetail" }>; ctx: BlockCtx }) {
  const t = screenText(ctx.locale);
  const detail = useRecordDetail(ctx.selection.record ?? undefined);
  if (!ctx.selection.record) return <Section><p className="text-sm text-muted-foreground">{t("pickRecord")}</p></Section>;
  if (detail.isLoading) return <Skeleton className="h-40" />;
  if (!detail.data) return <Section><p className="text-sm text-muted-foreground">{t("notFound")}</p></Section>;
  const { record, type, links } = detail.data;
  const fields = type.fields.filter((f) => !block.fields?.length || block.fields.includes(f.key));
  return (
    <Section title={<span className="flex items-center gap-2">{record.title} <Badge variant="outline" size="sm" className="font-mono">{record.number}</Badge></span>} description={pick(ctx.locale, type.name, type.nameAr)} testId="block-recordDetail"
      action={<Button size="sm" variant="ghost" onClick={() => ctx.select({})}><ArrowLeft className="rtl:rotate-180" /> {t("back")}</Button>}>
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.key} className={f.type === "longText" ? "sm:col-span-2" : undefined}>
            <dt className="text-xs text-muted-foreground">{pick(ctx.locale, f.label, f.labelAr)}</dt>
            <dd className="whitespace-pre-line text-sm">{display(f, record.data?.[f.key], ctx.locale)}</dd>
          </div>
        ))}
      </dl>
      {block.showLinks !== false ? (
        <div className="mt-4 border-t border-sand-200 pt-3">
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("links")}</p>
          {links.length === 0 ? <p className="text-sm text-muted-foreground">{t("noLinks")}</p> : (
            <ul className="space-y-1 text-sm">
              {links.map((l) => (
                <li key={l.id} className="flex items-center gap-2">
                  <Badge variant="outline" size="sm">{pick(ctx.locale, l.relationLabel ?? l.relationKey, l.relationLabelAr)}</Badge>
                  {l.caseId && ctx.caseHref ? <a className="text-aegold-700 hover:underline" href={ctx.caseHref(l.caseId)}>{l.caseNumber} · {l.caseTitle}</a> : <span>{l.recordNumber ?? l.caseNumber} · {l.recordTitle ?? l.caseTitle}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      {record.caseId ? (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("linkedCase")}:</span>
          <Button size="sm" variant="outline" onClick={() => ctx.select({ ...ctx.selection, case: record.caseId })}>{t("openCase")}</Button>
        </div>
      ) : null}
    </Section>
  );
}

// ---------------------------------------------------------------- reports

function ReportBlock({ block, ctx }: { block: Extract<ScreenBlock, { type: "report" }>; ctx: BlockCtx }) {
  const run = useReportRun(block.report);
  return (
    <Section title={pick(ctx.locale, block.title ?? run.data?.name, block.titleAr ?? run.data?.nameAr)} testId={`block-report-${block.report}`}>
      {run.isLoading ? <Skeleton className="h-48" /> : run.data ? <ReportView result={run.data} locale={ctx.locale} /> : <p className="text-sm text-muted-foreground">{(run.error as Error | null)?.message}</p>}
    </Section>
  );
}

function KpiBlock({ block, ctx }: { block: KpiBlockDef; ctx: BlockCtx }) {
  const run = useReportRun(block.report);
  const r = run.data;
  const index = r ? Math.max(0, block.column ? r.columns.findIndex((c) => c.key === block.column) : r.columns.findIndex((c) => c.type === "number" || c.type === "money")) : 0;
  const column = r?.columns[index];
  const value = r ? (r.rows.length === 1 ? r.rows[0]?.[index] : r.rows.reduce((s, row) => s + Number(row[index] ?? 0), 0)) : null;
  return (
    <div className="rounded-2xl border border-sand-200 bg-white/80 p-4" data-testid={`block-kpi-${block.report}`}>
      <div className="text-xs text-muted-foreground">{pick(ctx.locale, block.label ?? column?.label ?? r?.name, block.labelAr ?? column?.labelAr ?? r?.nameAr)}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{run.isLoading ? "…" : formatCell(value, ctx.locale)}</div>
    </div>
  );
}

// ---------------------------------------------------------------- services and cases

function ServiceStartBlock({ block, ctx }: { block: Extract<ScreenBlock, { type: "serviceStart" }>; ctx: BlockCtx }) {
  const t = screenText(ctx.locale);
  const intake = useServiceIntake(block.service, ctx.surface);
  const start = useStartService(block.service, ctx.surface);
  const [done, setDone] = useState<{ id: string; number: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  // The intake's long texts are kept on the device until the service starts with them (S 2.7).
  const formContext = useMemo(() => ({ draftKey: ctx.preview ? null : `screen:service:${block.service}:intake` }), [block.service, ctx.preview]);
  if (intake.isLoading) return <Skeleton className="h-48" />;
  if (!intake.data) return <Section><p className="text-sm text-muted-foreground">{t("unavailable")}</p></Section>;
  const s = intake.data;
  const submit = async (values: Record<string, unknown>) => {
    if (ctx.preview) return;
    setError(null);
    try {
      const r = await start.mutateAsync({ intake: values, locale: ctx.locale });
      setDone(r); setRound((n) => n + 1);
    } catch (e) {
      setError(saveFailureText(e));
    }
  };
  return (
    <Section title={pick(ctx.locale, block.label ?? s.name, block.labelAr ?? s.nameAr)} description={pick(ctx.locale, block.body ?? s.summary, block.bodyAr ?? s.summaryAr) || undefined} testId={`block-serviceStart-${block.service}`}>
      {done ? (
        <InlineAlert tone="success" title={t("done")}>
          {ctx.surface === "portal" ? t("submittedAs", { number: done.number }) : t("opened", { number: done.number })}
          {ctx.caseHref ? <> · <a className="font-medium underline" href={ctx.caseHref(done.id)}>{t("openCase")}</a></> : null}
        </InlineAlert>
      ) : null}
      {error ? <InlineAlert tone="danger" className="mt-2">{error}</InlineAlert> : null}
      {s.intakeForm ? (
        <FormRenderer key={round} document={s.intakeForm} locale={ctx.locale} mode="wizard" preview={ctx.preview} context={formContext} submitting={start.isPending} onSubmit={(v) => submit(v as Record<string, unknown>)} className="mt-3" />
      ) : (
        <div className="mt-2 space-y-2">
          <p className="text-sm text-muted-foreground">{t("noForm")}</p>
          <Button variant="gold" loading={start.isPending} disabled={ctx.preview} onClick={() => void submit({})}>{t("start")}</Button>
        </div>
      )}
    </Section>
  );
}

function CaseListBlock({ block, ctx }: { block: Extract<ScreenBlock, { type: "caseList" }>; ctx: BlockCtx }) {
  const t = screenText(ctx.locale);
  const rows = useCaseRows(block.service, ctx.surface);
  const list: CaseRow[] = rows.data ?? [];
  const open = (r: CaseRow) => (ctx.surface === "backoffice" ? ctx.select({ ...ctx.selection, case: r.id }) : ctx.caseHref && window.location.assign(ctx.caseHref(r.id)));
  return (
    <Section title={pick(ctx.locale, block.title, block.titleAr) || undefined} description={t("cases", { n: list.length })} testId={`block-caseList-${block.service}`}>
      {rows.isLoading ? <Skeleton className="h-32" /> : list.length === 0 ? <p className="text-sm text-muted-foreground">{t("noCases")}</p> : (
        <div className="overflow-x-auto rounded-xl border border-sand-200">
          <table className="w-full text-sm">
            <thead className="bg-sand-50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t("number")}</th>
                <th className="px-3 py-2 text-start font-medium">{t("title")}</th>
                <th className="px-3 py-2 text-start font-medium">{t("stage")}</th>
                <th className="px-3 py-2 text-start font-medium">{t("updated")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand-100">
              {list.map((r) => (
                <tr key={r.id} onClick={() => open(r)} className={cn("cursor-pointer hover:bg-sand-50", ctx.selection.case === r.id && "bg-aegold-50/60")}>
                  <td className="px-3 py-2 font-mono text-xs" dir="ltr">{r.number}</td>
                  <td className="px-3 py-2 font-medium">{pick(ctx.locale, r.title, r.titleAr)}</td>
                  <td className="px-3 py-2"><Badge variant="outline" size="sm">{pick(ctx.locale, r.stateLabel ?? r.state, r.stateLabelAr)}</Badge></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{date(r.updatedAt, ctx.locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function CaseDetailBlock({ block, ctx }: { block: Extract<ScreenBlock, { type: "caseDetail" }>; ctx: BlockCtx }) {
  const t = screenText(ctx.locale);
  const id = ctx.selection.case ?? undefined;
  const detail = useCaseDetail(id);
  const fire = useFireTransition(id ?? "");
  const [error, setError] = useState<string | null>(null);
  // A move that sends letters is put to the officer with the letters in full, and taken with their fingerprint (release 1.6).
  const m = useMicrocopy();
  const readLetters = useReadMoveLetters(id ?? "");
  const [pending, setPending] = useState<string | null>(null);
  const letters = useMoveLetters(id ?? "", pending);
  if (!id) return <Section><p className="text-sm text-muted-foreground">{t("pickCase")}</p></Section>;
  if (detail.isLoading) return <Skeleton className="h-48" />;
  const c = detail.data;
  if (!c) return <Section><p className="text-sm text-muted-foreground">{t("notFound")}</p></Section>;
  const manual = c.transitions.filter((x) => !x.automatic);
  const take = async (trigger: string, lettersFingerprint: string | null) => {
    try { await fire.mutateAsync({ trigger, lettersFingerprint }); setPending(null); } catch (e) { setError(saveFailureText(e)); }
  };
  const go = async (trigger: string) => {
    setError(null);
    try {
      if ((await readLetters(trigger)).letters.length > 0) { setPending(trigger); return; }
    } catch (e) { setError(saveFailureText(e)); return; }
    await take(trigger, null);
  };
  return (
    <Section title={<span className="flex items-center gap-2">{pick(ctx.locale, c.title, c.titleAr)} <Badge variant="outline" size="sm" className="font-mono">{c.number}</Badge></span>} testId="block-caseDetail"
      action={ctx.caseHref ? <a href={ctx.caseHref(c.id)}><Button size="sm" variant="outline">{t("openCase")}</Button></a> : null}>
      <ol className="mb-4 flex flex-wrap gap-1.5">
        {c.stages.map((s) => (
          <li key={s.key} className={cn("flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs", s.isCurrent ? "border-aegold-400 bg-aegold-50 font-medium text-aegold-800" : s.isDone ? "border-success-200 bg-success-50 text-success-700" : "border-sand-200 text-muted-foreground")}>
            {s.isDone ? <CheckCircle2 className="size-3" /> : s.isCurrent ? <CircleDot className="size-3" /> : null}{pick(ctx.locale, s.label, s.labelAr)}
          </li>
        ))}
      </ol>
      <div className="grid gap-4 lg:grid-cols-2">
        {block.showActions !== false && c.isOpen ? (
          <div data-testid="case-actions">
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("actions")}</p>
            {manual.length === 0 ? <p className="text-sm text-muted-foreground">{t("noActions")}</p> : (
              <div className="space-y-1.5">
                {manual.map((x) => (
                  <div key={x.trigger}>
                    <Button size="sm" variant="outline" className="w-full justify-start" disabled={ctx.preview || !x.guardSatisfied || !x.permittedForCurrentUser} loading={fire.isPending && fire.variables?.trigger === x.trigger} onClick={() => void go(x.trigger)}>
                      {pick(ctx.locale, x.label, x.labelAr)}
                    </Button>
                    {!x.guardSatisfied ? <p className="mt-0.5 text-[11px] text-muted-foreground">{t("guardUnmet")}</p> : !x.permittedForCurrentUser ? <p className="mt-0.5 text-[11px] text-muted-foreground">{t("notPermitted")}</p> : null}
                  </div>
                ))}
              </div>
            )}
            {pending ? (
              <div className="mt-3 space-y-2 rounded-lg border border-sand-200 p-3" data-testid="move-letters">
                <StepLetterSheets preview={letters} className="max-h-[50vh] overflow-y-auto" />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setPending(null)}>{t("cancel")}</Button>
                  <Button size="sm" variant="gold" loading={fire.isPending} disabled={!letters.shown || letters.letters.length === 0} onClick={() => void take(pending, letters.fingerprint)}>{m("stepLettersConfirm")}</Button>
                </div>
              </div>
            ) : null}
            {error ? <InlineAlert tone="danger" className="mt-2">{error}</InlineAlert> : null}
          </div>
        ) : null}
        {block.showTasks !== false ? (
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("tasks")}</p>
            {c.tasks.length === 0 ? <p className="text-sm text-muted-foreground">{t("noTasks")}</p> : (
              <ul className="space-y-1 text-sm">
                {c.tasks.slice(0, 8).map((k) => (
                  <li key={k.id} className="flex items-center gap-2">
                    {k.isOpen ? <CircleDot className="size-3.5 text-aegold-600" /> : <CheckCircle2 className="size-3.5 text-success-600" />}
                    <span className="flex-1 truncate">{pick(ctx.locale, k.title, k.titleAr)}</span>
                    {k.dueAt && k.isOpen ? <span className="text-[11px] text-muted-foreground">{t("due", { date: date(k.dueAt, ctx.locale) })}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
        {block.showTimeline !== false ? (
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("timeline")}</p>
            {c.events.length === 0 ? <p className="text-sm text-muted-foreground">{t("noEvents")}</p> : (
              <ul className="space-y-1.5 text-sm">
                {c.events.slice(0, 8).map((e) => (
                  <li key={e.id}><span className="text-[11px] text-muted-foreground">{date(e.occurredAt, ctx.locale)}</span> · {pick(ctx.locale, e.title, e.titleAr)}{e.actorName ? <span className="text-muted-foreground"> · {e.actorName}</span> : null}</li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
        {block.showDocuments !== false ? (
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("documents")}</p>
            {c.documents.length === 0 ? <p className="text-sm text-muted-foreground">{t("noDocuments")}</p> : (
              <ul className="space-y-1 text-sm">{c.documents.slice(0, 8).map((d) => <li key={d.id} className="flex items-center gap-2"><FileText className="size-3.5 text-muted-foreground" /> <span className="truncate">{d.fileName}</span></li>)}</ul>
            )}
          </div>
        ) : null}
      </div>
    </Section>
  );
}
